import { useFeatureQuery } from '@common/hooks/query/useFeatureQuery';
import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    InitialQueryState,
    SkipOrArgs,
} from '@common/types/queryState';
import { composeGenerators } from '@common/utils/query/composeGenerators';
import { useSelectedProductionId } from '@features/productions';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useGetClipHistoryQuery } from '../api';
import { WORKFLOW_KEYS } from '../constants';

import { isEmpty } from '@common/utils/typeGuards';
import { GetClipsArgs } from '../types';

export interface UseClipWorkflowsArgs extends BaseFeatureArgs {
    mediaObjectIds: string[];
}

const clipWorkflowsFilterGenerator: FixedFilterGenerator<
    UseClipWorkflowsArgs
> = (args) => {
    return [
        `mediaObjectId:(${args.mediaObjectIds.map((id) => `"${id}"`).join(' ')})`,
    ];
};

export const useClipWorkflows = (
    initialArgs: InitialQueryState = {},
    args: SkipOrArgs<UseClipWorkflowsArgs>,
) => {
    const productionId = useSelectedProductionId();

    const queryArgs: SkipOrArgs<GetClipsArgs> = useMemo(() => {
        if (args === skipToken || !productionId || isEmpty(args.mediaObjectIds))
            return skipToken;

        const mergedGenerators = composeGenerators(
            clipWorkflowsFilterGenerator,
            args.fixedFilterGenerators,
        );

        return {
            ...args,
            productionId,
            facetFields: ['status'],
            searchTargetFields: ['clipName'],
            keyMap: WORKFLOW_KEYS,
            fixedFilterGenerators: mergedGenerators,
        } as GetClipsArgs;
    }, [args, productionId]);

    return useFeatureQuery(initialArgs, useGetClipHistoryQuery, queryArgs);
};
